/**
 * Atelier Guillot — cohérence du panier pour les options payantes du configurateur (assets/guillot-perso.js).
 *
 * Les options (porte-clés assorti, texte en bas, logo de modèle, verso, logo par e-mail) sont des lignes à part : les produits
 * cachés d'Aris, reliés au produit par _apo_order (contrat inchangé). Le panier du thème ne les relie pas : changer la quantité
 * du porte-carte laissait les options à l'ancienne quantité (constaté le 17/10/2026). Ce script, après chaque mise à jour du panier :
 *   - aligne la quantité de chaque option sur celle de son produit (× k de « Added by: <id> * k ») ;
 *   - retire une option dont le produit a été retiré (orpheline) ;
 *   - ré-ajoute une option disparue alors que son produit est encore là (les propriétés du produit l'annoncent) ;
 *   - verrouille les lignes d'option dans l'interface (ni quantité ni suppression : on agit sur le produit).
 * Il ne touche QUE les lignes créées par le configurateur dans ce navigateur (registre localStorage « gp:commandes ») :
 * les lignes posées par Aris sur les fiches non migrées restent exactement comme avant.
 */
import { CartUpdatedEvent, ThemeEvents } from '@theme/events';

const REGISTRE = 'gp:commandes';
const lire = () => { try { return JSON.parse(localStorage.getItem(REGISTRE) || '[]'); } catch (e) { return []; } };
let enCours = false, relance = false;

export function enregistrer(id, options) {
  try {
    const r = lire().filter((x) => Date.now() - x.t < 30 * 864e5 && x.id !== id);
    r.push({ id, t: Date.now(), opts: options.map((o) => ({ v: o.id, apo: o.apo })) });
    localStorage.setItem(REGISTRE, JSON.stringify(r.slice(-60)));
  } catch (e) { /* navigation privée : la synchronisation se limite à la session */ }
}

function idDe(ligne) {
  const o = ligne.properties?._apo_order;
  if (!o) return null;
  const m = String(o).match(/^Added by:\s*(\d+)\s*(?:\*\s*(\d+))?/);
  return m ? { id: m[1], k: m[2] ? Number(m[2]) : 0, option: true } : { id: String(o), option: false };
}

function sectionsTiroir() {
  const t = document.querySelector('cart-drawer');
  return t?.dataset.sectionId ? [t.dataset.sectionId, 'auto-gifts'] : [];
}

async function synchroniser() {
  const registre = lire();
  if (!registre.length) return;
  if (enCours) { relance = true; return; }
  enCours = true;
  try {
    const nos = new Map(registre.map((x) => [x.id, x]));
    const panier = await fetch('/cart.js').then((r) => r.json());
    const produits = new Map(), options = [];
    panier.items.forEach((l, i) => {
      const x = idDe(l);
      if (!x || !nos.has(x.id)) return;
      if (x.option) options.push({ l, i, ...x }); else produits.set(x.id, l);
    });
    const maj = {}, ajouts = [];
    options.forEach(({ l, id, k }) => {
      const p = produits.get(id);
      const voulu = p ? (k ? p.quantity * k : 1) : 0;
      if (l.quantity !== voulu) maj[l.key] = voulu;
    });
    produits.forEach((p, id) => {
      nos.get(id).opts.forEach((o) => {
        const present = options.some((x) => x.id === id && x.l.variant_id === o.v && x.l.properties._apo_option === o.apo);
        if (!present) ajouts.push({ id: o.v, quantity: p.quantity, properties: { _apo_option: o.apo, _apo_order: `Added by: ${id} * 1` } });
      });
    });
    marquer(panier);
    if (!Object.keys(maj).length && !ajouts.length) return;
    const sections = sectionsTiroir();
    let rep = null;
    if (Object.keys(maj).length) {
      rep = await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: maj, sections: sections.join(','), sections_url: location.pathname }) }).then((r) => r.json());
    }
    if (ajouts.length) {
      rep = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: ajouts, sections: sections.join(','), sections_url: location.pathname }) }).then((r) => r.json());
    }
    if (/\/cart\/?$/.test(location.pathname)) { location.reload(); return; }
    const total = await fetch('/cart.js').then((r) => r.json());
    document.dispatchEvent(new CartUpdatedEvent({}, { itemCount: total.item_count, source: 'guillot-panier', sections: rep?.sections }));
  } catch (e) {
    /* jamais d'erreur visible : le panier reste celui du thème */
  } finally {
    enCours = false;
    if (relance) { relance = false; setTimeout(synchroniser, 50); }
  }
}

/** Lignes d'option : ni quantité ni suppression (on agit sur le produit, l'option suit). */
function marquer(panier) {
  const nos = new Set(lire().map((x) => x.id));
  const verrou = new Set();
  panier.items.forEach((l, i) => { const x = idDe(l); if (x?.option && nos.has(x.id)) verrou.add(i + 1); });
  document.querySelectorAll('cart-item[data-index], cart-drawer-item[data-index], [data-ref="cart-item"][data-index]').forEach((el) => {
    el.classList.toggle('gp-ligne-option', verrou.has(Number(el.dataset.index)));
  });
}

if (!window.guillotPanier) {
  window.guillotPanier = { synchroniser, enregistrer };
  const style = document.createElement('style');
  style.textContent = '.gp-ligne-option quantity-selector,.gp-ligne-option .quantity-selector,.gp-ligne-option [name="minus"],.gp-ligne-option [name="plus"],.gp-ligne-option [data-ref*="remove"],.gp-ligne-option button[aria-label*="emove"],.gp-ligne-option button[aria-label*="upprim"]{display:none!important}';
  document.head.appendChild(style);
  document.addEventListener(ThemeEvents.cartUpdated, (e) => { if (e.detail?.data?.source !== 'guillot-panier') setTimeout(synchroniser, 60); });
  synchroniser();
}
